import React from "react"
import * as React_ from "elm-ts/lib/React"
import * as A from "fp-ts/Array"
import * as E from "fp-ts/Either"
import * as Tuple from "fp-ts/Tuple"
import * as O from "fp-ts/Option"
import * as R from "fp-ts/Record"
import * as Router from "../../routes"
import * as M from "../Model/overviewModel"
import {
  AgentList,
  PropertyDetail,
  Error,
  ValuationDetail,
  OfferList,
  ValuationList,
  CalendarWidget,
} from "../components"
import { Modal, Icon, BrandSpinner } from "../../components"
import { flow, increment, pipe } from "fp-ts/lib/function"
import {
  AgentOverview,
  AgentOfferDetails,
  sortByOfferDate,
  Variant,
} from "@flypengine/myFlyp-lib"
import { GDRIVE_BASE_URL } from "../../runtimeVariables"
import { numberAbreviation, ordinalNumber } from "../../lib/format"
import dayjs from "dayjs"
import { boolean } from "fp-ts"

export function overviewView<msg>(
  config: M.Config<msg>
): (model: M.Model) => React_.Html<msg> {
  return model => dispatch =>
    pipe(
      model,
      E.match(
        status => {
          switch (status) {
            case "Loading": {
              return (
                <div data-cy="dashboard-overview-loader">
                  <BrandSpinner description="Loading deal's data" />
                </div>
              )
            }
            case "Error": {
              return (
                <Error
                  dataCy="dashboard-overview-error"
                  message=" Data not found. If this doesn't look right please"
                />
              )
            }

            case "Not Authorized": {
              return (
                <Error
                  dataCy="dashboard-overview-error-auth"
                  message="You are not authorized to see this content. If this doesn't look right please"
                />
              )
            }

            default: {
              return <></>
            }
          }
        },
        model => (
          <div className="layout-grid">
            <div className="layout-col">
              <h4 className="heading">The property</h4>
              {viewPropertyDetails({
                contents: model.propertyInfo,
                updateModalContents: contents =>
                  pipe(
                    contents,
                    M.Modal.PropertyDetails,
                    O.of,
                    M.Msg.modalContents
                  ),
              })(flow(config.wrapperMsg, dispatch))}
              {viewPropertyWorth({
                contents: model.propertyWorth,
                modal: model.modal,
                updateContents: contents =>
                  pipe(contents, M.Msg.propertyWorthModel),
                updateModalContents: contents =>
                  pipe(
                    contents,
                    M.Modal.ValuationDetail,
                    O.of,
                    M.Msg.modalContents
                  ),
                updateLastFocused: flow(O.of, M.Msg.lastFocused),
                updateOfferModalContents: contents =>
                  pipe(
                    contents,
                    M.Modal.OfferDetail,
                    O.of,
                    M.Msg.modalContents
                  ),
              })(flow(config.wrapperMsg, dispatch))}
            </div>
            <div className="layout-col">
              <h4 className="heading">Status</h4>
              {viewMarketShare()(flow(config.wrapperMsg, dispatch))}
              {viewDealStatus({
                contents: model.statusModel,
                msg: M.Msg.statusModel,
              })(flow(config.wrapperMsg, dispatch))}
            </div>
            <div className="layout-col third-col">
              <h4 className="heading">Agents and Viewings</h4>
              <div className="grid grid-rows-[5fr_4fr] grid-cols-1 gap-6">
                {viewAgents({
                  contents: model.agentsModel,
                  msg: M.Msg.agentsModel,
                })(flow(config.wrapperMsg, dispatch))}
                {viewViewings(model.agentViewingsModel)(
                  flow(config.wrapperMsg, dispatch)
                )}
              </div>
              {viewUpcommingViewings({
                contents: model.upcommingViewingsModel,
                tabChanged: config.tabChangedMsg,
              })(dispatch)}
            </div>
            {modalView(model.modal)(flow(config.wrapperMsg, dispatch))}
          </div>
        )
      )
    )
}

function viewPropertyWorth({
  contents,
  updateContents,
  updateModalContents,
  updateLastFocused,
  updateOfferModalContents,
}: {
  contents: M.PropertyWorthModel
  modal: O.Option<M.Modal>
  updateOfferModalContents: (contents: M.OfferDetail) => M.Msg
  updateModalContents: (contents: M.ValuationDetail) => M.Msg
  updateLastFocused: (id: string) => M.Msg
  updateContents: (page: M.PropertyWorthModel) => M.Msg
}): React_.Html<M.Msg> {
  return dispatch => {
    const Pagination = ({
      label,
      page,
    }: {
      label: string
      page: M.PropertyWorthPagination
    }) => (
      <button
        className="my-4 underline cursor-pointer"
        data-cy="dashboard-property-worth-navigation"
        onClick={() => dispatch(updateContents({ ...contents, current: page }))}
      >
        {label}
      </button>
    )
    {
      switch (contents.current) {
        case "offer": {
          return (
            <div className="card h-full">
              <OfferList
                offers={M.filterRecentOffers(contents.offers, contents.agents)}
                onSelectOffer={(data, id) => {
                  dispatch(updateLastFocused(id))
                  dispatch(
                    updateOfferModalContents({
                      agent: data.agent,
                      offers: pipe(
                        contents.offers,
                        R.filterMapWithIndex((buyerName, offers) =>
                          data.offer.buyer === buyerName
                            ? O.some(offers)
                            : O.none
                        ),
                        R.toArray,
                        A.map(Tuple.snd),
                        A.flatten
                      ),
                    })
                  )
                }}
              />
              {A.isNonEmpty(contents.valuations) && (
                <Pagination label="View Valuations" page="valuation" />
              )}
            </div>
          )
        }
        case "valuation": {
          return A.isEmpty(contents.valuations) ? (
            <div className="card bg-stone-light">
              <div
                className="card flex flex-col h-full"
                data-cy={"dashboard-valuations-empty"}
              >
                <div className="grow flex flex-col items-center  justify-center text-center">
                  <h4 className="heading text-4xl text-brick mb-6">
                    Valuations
                  </h4>
                  <p>
                    We’re getting your listing ready for valuations. Watch this
                    space!
                  </p>
                  <a
                    href="https://api.whatsapp.com/send/?phone=447723477585&text&type=phone_number&app_absent=0"
                    className="button primary mt-6"
                  >
                    Contact us
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="card h-full">
              <ValuationList
                valuations={contents.valuations}
                onSelect={(modalContents, id) => {
                  dispatch(updateLastFocused(id))
                  dispatch(updateModalContents(modalContents))
                }}
              />
              {!R.isEmpty(contents.offers) && (
                <Pagination label="View Offers" page="offer" />
              )}
            </div>
          )
        }
      }
    }
  }
}

function viewAgents({
  contents,
  msg,
}: {
  contents: O.Option<M.AgentsModel>
  msg: (m: M.AgentsModel) => M.Msg
}): React_.Html<M.Msg> {
  return dispatch =>
    pipe(
      contents,
      O.match(
        () => (
          <div
            className="card flex flex-col items-center justify-center"
            data-cy={"dashboard-agents-empty"}
          >
            <div className=" marker bg-brick text-white heading text-sm text-center mb-3 h-14 ">
              Coming soon!
            </div>
            <h2 className="heading text-center">Preparing your agents</h2>
          </div>
        ),
        ({ showAgents, agents, stage }) => {
          const acceptedAgents: number = pipe(
            agents,
            A.reduce(0, (acc, elem) =>
              pipe(
                elem.agentDealStatus,
                O.exists(status => status === "ACCEPTED"),
                boolean.fold(
                  () => acc,
                  () => increment(acc)
                )
              )
            )
          )

          const nonRejectedAgents: number = pipe(
            agents,
            A.reduce(0, (acc, elem) =>
              pipe(
                elem.agentDealStatus,
                O.fold(
                  () => true,
                  status => status !== "REJECTED"
                ),
                boolean.fold(
                  () => acc,
                  () => increment(acc)
                )
              )
            )
          )

          const Summary = () => {
            switch (stage) {
              case "prelaunch":
                return <></>

              case "valuation": {
                return (
                  <>
                    {agents.length === 1
                      ? `${agents.length} valuation request sent`
                      : `${agents.length} valuation requests sent`}
                  </>
                )
              }
              case "instruction": {
                return (
                  <div className="flex flex-col">
                    <span>
                      {agents.length === 1
                        ? `${nonRejectedAgents} agent instructed`
                        : `${nonRejectedAgents} agents instructed`}
                    </span>
                    <span>
                      {acceptedAgents > 0 ? (
                        `${acceptedAgents} agents accepted`
                      ) : (
                        <></>
                      )}
                    </span>
                  </div>
                )
              }
            }
          }
          return (
            <>
              {showAgents ? (
                <AgentList
                  agents={agents}
                  onClickMsg={() =>
                    dispatch(msg({ agents, stage, showAgents: false }))
                  }
                />
              ) : (
                <div className="card flex flex-col items-center justify-between">
                  <h2 className="heading" data-cy={"dashboard-agents-counter"}>
                    <Summary />
                  </h2>
                  <button
                    className="button primary"
                    data-cy="dashboard-agents-viewall"
                    onClick={() =>
                      dispatch(msg({ agents, stage, showAgents: true }))
                    }
                  >
                    See all agents
                  </button>
                </div>
              )}
            </>
          )
        }
      )
    )
}

function viewViewings(
  contents: O.Option<M.AgentViewingsModel>
): React_.Html<M.Msg> {
  const EmptyState = () => (
    <div
      className="card flex flex-col items-center justify-between"
      data-cy={"dashboard-viewings-empty"}
    >
      <p className="text leading-none">No viewings yet, we're on it!</p>

      <a
        href="https://api.whatsapp.com/send/?phone=447723477585&text&type=phone_number&app_absent=0"
        className="button primary block max-w-max "
      >
        Contact us
      </a>
    </div>
  )
  return _dispatch =>
    pipe(
      contents,
      O.match(
        () => <EmptyState />,
        contents => {
          return contents.viewings === 0 ? (
            <EmptyState />
          ) : (
            <div className="card flex flex-col justify-between">
              <p>To date</p>
              <div className="flex items-end gap-2">
                <h5
                  className="heading text-5xl leading-[0.9]"
                  data-cy={"dashboard-viewings-counter"}
                >
                  {contents.viewings}
                </h5>
                <p data-cy={"dashboard-viewings-counter-label"}>
                  {contents.viewings === 1 ? "viewing" : "viewings"} by{" "}
                </p>
                <h5
                  className=" ml-2 heading text-5xl leading-[0.9] "
                  data-cy={"dashboard-viewings-agent-counter"}
                >
                  {contents.agents}
                </h5>
                <p data-cy={"dashboard-viewings-agent-counter-label"}>
                  {contents.agents === 1 ? "agent" : "agents"}
                </p>
              </div>
            </div>
          )
        }
      )
    )
}

function viewUpcommingViewings<msg>({
  contents,
  tabChanged,
}: {
  contents: O.Option<M.UpcommingViewingsModel>
  tabChanged: (tab: Router.Tab) => msg
}): React_.Html<msg> {
  return dispatch => {
    return pipe(
      contents,
      O.match(
        () => (
          <CalendarWidget
            title="We are not live just yet..."
            label="this week"
            counter={0}
            showCalendarIcon={true}
            dataCy="dashboard-upcomming-viewings"
          />
        ),
        ({ status, month, week }) => {
          switch (status) {
            case "marketplaceLaunched": {
              return (
                <CalendarWidget
                  title="Agents contacting their buyers"
                  label={`${
                    week !== 0 || month === 0
                      ? "Viewings this week"
                      : "Viewings this month"
                  }`}
                  counter={week !== 0 || month === 0 ? week : month}
                  showCalendarIcon={false}
                  onClickMsg={() => pipe("viewings", tabChanged, dispatch)}
                  dataCy="dashboard-upcomming-viewings"
                />
              )
            }
            case "agentViewings":
            case "offers": {
              return (
                <CalendarWidget
                  title="Upcoming viewings"
                  label={`${
                    week !== 0 || month === 0 ? "this week" : "this month"
                  }`}
                  counter={week !== 0 || month === 0 ? week : month}
                  showCalendarIcon={false}
                  onClickMsg={() => pipe("viewings", tabChanged, dispatch)}
                  dataCy="dashboard-upcomming-viewings"
                />
              )
            }

            default: {
              return (
                <CalendarWidget
                  title="We are not live just yet..."
                  label="this week"
                  counter={0}
                  showCalendarIcon={true}
                  onClickMsg={() => pipe("viewings", tabChanged, dispatch)}
                  dataCy="dashboard-upcomming-viewings"
                />
              )
            }
          }
        }
      )
    )
  }
}

function viewDealStatus({
  contents,
  msg,
}: {
  contents: M.StatusModel
  msg: (m: M.StatusModel) => M.Msg
}): React_.Html<M.Msg> {
  return dispatch => (
    <div className="card md:px-8 pb-8 flex flex-col">
      {pipe(
        contents.statuses,
        A.map(stage =>
          stage.active ? (
            <React.Fragment key={stage.id}>
              <div>
                <h4 className="heading">Status</h4>
                {stage.complete && (
                  <div
                    className="hidden absolute top-5 right-7 w-[32px] h-[32px] bg-lawn rounded-full md:grid place-items-center "
                    data-cy={`dashboard-deal-status-complete-${stage.id}`}
                  >
                    <div className="w-[24px]">
                      <Icon.Checkmark />
                    </div>
                  </div>
                )}
              </div>
              <div className="status-shape grow px-4 ">
                <div
                  className={stage.id === contents.current ? "" : " opacity-50"}
                  data-cy={`dashboard-deal-status-contents-${stage.id}`}
                >
                  <h3
                    className="heading text-2xl md:text-3xl mb-2 leading-none"
                    data-cy={"dashboard-deal-status"}
                  >
                    {stage.title}
                  </h3>
                  {pipe(
                    stage.checklist,
                    A.map(({ item, complete }) =>
                      complete ? (
                        <div
                          key={item}
                          className="flex gap-3 [--icon-fill:white]"
                          data-cy={`dashboard-deal-status-item-complete-${stage.id}`}
                        >
                          <span className="hidden md:block w-[17px]">
                            <Icon.CheckboxChecked />
                          </span>
                          <p
                            className="leading-tight"
                            data-cy="dashboard-status-checklist-item"
                          >
                            {item}
                          </p>
                        </div>
                      ) : (
                        <div
                          key={item}
                          className="flex gap-3 [--icon-fill:white]"
                        >
                          <span className="hidden md:block w-[17px]">
                            <Icon.CheckboxUnchecked />
                          </span>
                          <p
                            className="leading-tight"
                            data-cy="dashboard-status-checklist-item"
                          >
                            {item}
                          </p>
                        </div>
                      )
                    )
                  )}
                </div>
              </div>
              <div
                className=" mb-4 shrink-0 status-tic bg-glass-light  "
                style={
                  {
                    "--n": stage.index,
                    "--total": contents.statuses.length,
                  } as React.CSSProperties
                }
              ></div>
            </React.Fragment>
          ) : (
            <React.Fragment key={stage.id} />
          )
        )
      )}

      <div className=" shrink-0  h-4 flex gap-3 relative ">
        {pipe(
          contents.statuses,
          A.map(stage => (
            <button
              key={stage.id}
              onClick={() =>
                dispatch(
                  msg({
                    current: contents.current,
                    statuses: pipe(
                      contents.statuses,
                      A.map(({ title, checklist, complete, id, index }) => ({
                        id,
                        title,
                        checklist,
                        complete,
                        index,
                        active: id === stage.id,
                      }))
                    ),
                  })
                )
              }
              className={
                "grow bg-glass-light rounded-xl" +
                (stage.active ? "" : " opacity-50")
              }
              data-cy={`dashboard-deal-status-${stage.id}`}
            ></button>
          ))
        )}
      </div>
    </div>
  )
}

function viewMarketShare(): React_.Html<M.Msg> {
  return _dispatch => (
    <div className="card flex flex-col" data-cy={"dashboard-valuations-empty"}>
      <div className="grow flex flex-col items-center justify-center text-center">
        <h4 className="heading text-4xl text-brick mb-6">
          Welcome to your Marketplace!
        </h4>
        <p className="max-w-[16em]">
          From viewings to offers, here you’ll be able to follow the engagement
          on your home as it happens.
        </p>
      </div>
    </div>
  )
}

function viewPropertyDetails({
  contents,
  updateModalContents,
}: {
  contents: M.PropertyDetails
  updateModalContents: (contents: M.PropertyDetails) => M.Msg
}): React_.Html<M.Msg> {
  return dispatch => (
    <div className="card">
      <div className=" h-full bg-gray-400 relative">
        {pipe(
          contents.images,
          A.head,
          O.match(
            () => <></>,
            id => (
              <img
                className=" w-full h-full object-cover"
                src={`${GDRIVE_BASE_URL}/${id}`}
                loading="lazy"
                alt="property photo"
              />
            )
          )
        )}
        <div className=" absolute top-0 left-0 right-0 bottom-0 text-center text-white py-6 overflow-hidden ">
          <div className="w-full h-full absolute top-0 bg-slate opacity-40 "></div>
          <div className="relative h-full">
            <h5
              className="heading text-white px-4"
              data-cy={"dashboard-property-address"}
            >
              {contents.address}
            </h5>

            {A.isNonEmpty(contents.images) ? (
              <button
                className=" button primary absolute bottom-0 left-1/2 -translate-x-1/2 whitespace-pre"
                onClick={() => pipe(contents, updateModalContents, dispatch)}
              >
                View images
              </button>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export const offerDetailView = ({
  offers,
  agent,
}: {
  agent: AgentOverview
  offers: AgentOfferDetails[]
}): React_.Html<M.Msg> => {
  return _ => {
    return (
      <div className="w-full">
        <div>
          {pipe(
            offers,
            A.head,
            O.match(
              () => <></>,
              offer => {
                const { name, value } = numberAbreviation(offer.amount)
                const offerStatus = M.offerStatusLabel(offer.status)
                return (
                  <>
                    <div
                      className=" h-full overflow-auto"
                      data-cy="dashboard-offer-modal"
                    >
                      <div className="mb-2">
                        <h3 className="heading text-2xl sm:text-3xl ">
                          You
                          <span className="bg-tile py-1 px-2">received</span> an
                          Offer:
                          <span className="py-6 block text-5xl sm:text-7xl text-center tracking-[0.03em]">
                            £ {value} {O.getOrElse(() => "")(name)}
                          </span>
                        </h3>
                      </div>
                      <dl className="mt-10 grid grid-cols-[auto_1fr] sm:grid-cols-[120px_1fr] gap-x-4 gap-y-4 sm:gap-y-1 text-base sm:text-lg sm:items-center ">
                        <dt>Status</dt>
                        <dd className="tag bg-tile-light text-sm uppercase ">
                          {offerStatus}
                        </dd>
                        <dt>Buyer</dt>
                        <dd>{offer.buyer}</dd>
                        <dt>Offer date</dt>
                        <dd>
                          {dayjs(offer.dateOfOffer).format("ddd MMM DD YYYY")}
                        </dd>
                        <dt>Agent</dt>
                        <dd>
                          {agent.brand} {O.getOrElse(() => "")(agent.branch)}
                        </dd>
                        <dt>Financial</dt>
                        <dd className="text-sm flex flex-wrap gap-2 items-center">
                          {pipe(
                            offer.financialPosition,
                            R.filterWithIndex((_, flag) => flag),
                            R.toEntries,
                            A.map(([value, _]) => (
                              <span
                                key={value}
                                className="tag bg-tile-light text-sm  "
                              >
                                {value.toUpperCase()}
                              </span>
                            ))
                          )}
                        </dd>
                        {pipe(
                          offer.finPosTerms,
                          O.match(
                            () => <></>,
                            finPosTerms => (
                              <>
                                <dt>Financial Terms</dt>
                                <dd className="capitalize">
                                  {finPosTerms.toLowerCase()}
                                </dd>
                              </>
                            )
                          )
                        )}
                        {pipe(
                          offer.mortgageStatus,
                          O.match(
                            () => <></>,
                            mortgageStatus => (
                              <>
                                <dt>Mortgage</dt>
                                <dd className="capitalize">
                                  {mortgageStatus.toLowerCase()}
                                </dd>
                              </>
                            )
                          )
                        )}
                        <dt>In Chain</dt>
                        <dd>{offer.inChain ? "Yes" : "No"}</dd>
                        <dt>Solicitor</dt>
                        <dd>{offer.hasSolicitors ? "Yes" : "None"}</dd>
                        <dt>Furniture</dt>
                        <dd>
                          {offer.furnitureIncluded ? "Included" : "Excluded"}
                        </dd>
                      </dl>
                      <div className="mt-20">
                        <h3 className="mb-5">History</h3>
                        <div className="mb-10 flex flex-col gap-6 xs:gap-2 text-lg">
                          {pipe(
                            sortByOfferDate(offers, "ASC"),
                            A.mapWithIndex((i, offer) => {
                              const { name, value } = numberAbreviation(
                                offer.amount
                              )
                              const label = M.offerStatusLabel(offer.status)
                              return (
                                <p
                                  key={`${offer.agentId}-${i}`}
                                  data-cy={`offer-history-entry-${i}`}
                                  className="font-mono flex flex-wrap gap-6 md:gap-10 gap-y-0"
                                >
                                  <span className=" ">
                                    {dayjs(offer.dateOfOffer).format(
                                      "dd MMM DD YYYY"
                                    )}
                                  </span>
                                  {ordinalNumber(i + 1)}
                                  {" offer"}
                                  <span>
                                    £ {value} {O.getOrElse(() => "")(name)}
                                  </span>
                                  <span className="capitalize">
                                    {pipe(
                                      offer.fellThroughReason,
                                      O.match(
                                        () => label,
                                        fellThroughReason =>
                                          `${offerStatus} - ${fellThroughReason}`
                                      )
                                    )}
                                  </span>
                                </p>
                              )
                            })
                          )}
                        </div>
                      </div>
                    </div>
                    {pipe(
                      offer.linkToProof,
                      O.match(
                        () => <></>,
                        linkToProof => (
                          <div className="card px-6 py-2 bg-stone-light inline-block">
                            {linkToProof.includes("pdf") ? (
                              <a
                                href={linkToProof}
                                data-cy="offer-proof-link"
                                target="_blank"
                                rel="noreferrer noopener"
                              >
                                <span>View Offer Proof</span>
                                <span className="ml-3 inline-block w-3 h-3">
                                  <Icon.ExternalLink />
                                </span>
                              </a>
                            ) : (
                              <a
                                href={linkToProof}
                                data-cy="offer-proof-link-with-preview"
                                target="_blank"
                                rel="noreferrer noopener"
                              >
                                <img
                                  src={linkToProof}
                                  alt="Image of email with offer"
                                />
                              </a>
                            )}
                          </div>
                        )
                      )
                    )}
                  </>
                )
              }
            )
          )}
        </div>
      </div>
    )
  }
}

function modalView(modal: O.Option<M.Modal>): React_.Html<M.Msg> {
  return Modal({
    isOpen: pipe(modal, O.isSome),
    closeMsg: M.Msg.modalContents(O.none),
    containerStyles: "card rounded-[14px]",
    body: dispatch =>
      pipe(
        modal,
        O.match(
          () => <></>,
          contents =>
            pipe(
              contents,
              Variant.caseOf({
                [M._ValuationDetail]: contents => (
                  <ValuationDetail
                    {...contents}
                    updateModalContents={flow(
                      M.Modal.ValuationDetail,
                      O.of,
                      M.Msg.modalContents,
                      dispatch
                    )}
                  />
                ),
                [M._PropertyDetails]: contents => (
                  <PropertyDetail
                    {...contents}
                    updateModalContents={flow(
                      M.Modal.PropertyDetails,
                      O.of,
                      M.Msg.modalContents,
                      dispatch
                    )}
                  />
                ),
                [M._OfferDetail]: contents => (
                  <>{offerDetailView(contents)(dispatch)}</>
                ),
              })
            )
        )
      ),
  })
}
